import { PropsWithChildren, Children } from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";

import { media } from "@atlaskit/primitives/responsive";

interface childrenTypes {
    1?: string | undefined;
    2?: string | undefined;
    3?: string | undefined;
    4?: string | undefined;
    5?: string | undefined;
    6?: string | undefined;
    7?: string | undefined;
    8?: string | undefined;
    9?: string | undefined;
    10?: string | undefined;
    11?: string | undefined;
    12?: string | undefined;
    optional?: string | undefined;
}

interface childGridsTypes {
    0?: childrenTypes;
    1?: childrenTypes;
    2?: childrenTypes;
    3?: childrenTypes;
    4?: childrenTypes;
}

const gridOrganizers = (
    breakpoint: number,
    gridCols: Array<string>,
    gridRows?: Array<string>,
    customChildGrids?: childGridsTypes
) => {
    const customChildGridsBreakpoint = customChildGrids
        ? customChildGrids[breakpoint as keyof childGridsTypes]
        : false;

    return `
  grid-template-columns: ${gridCols[breakpoint]};
  ${
      gridRows && gridRows[breakpoint]
          ? `grid-template-rows: ${gridRows[breakpoint]};`
          : ``
  }
  ${
      customChildGrids && customChildGridsBreakpoint
          ? Object.keys(customChildGridsBreakpoint)
                .map(
                    (child) => `
          > *:nth-of-type(${child}) {
            ${
                customChildGridsBreakpoint[
                    child as keyof typeof customChildGridsBreakpoint
                ]
            };
          } 
        `
                )
                .join("")
          : ``
  }`;
};
const fixedPadding = 60;
const maxContentWidth = 1152;

const gridStyles = (
    gridCols: Array<string>,
    gridRows?: Array<string>,
    customChildGrids?: childGridsTypes,
    isReduced?: boolean
) => css`
    display: grid;
    justify-content: space-between;
    margin: auto;
    max-width: 100%;
    box-sizing: border-box;
    gap: ${isReduced ? "0" : "24px"};
    padding-inline-start: 32px;
    padding-inline-end: 32px;
    ${gridOrganizers(0, gridCols, gridRows, customChildGrids)}
    & + .row-layout {
        margin-block-start: 24px;
    }

    ${media.above.sm} {
        padding-inline-start: ${fixedPadding}px;
        padding-inline-end: ${fixedPadding}px;
        ${gridOrganizers(1, gridCols, gridRows, customChildGrids)};

        & + .row-layout {
            margin-block-start: 32px;
        }
    }

    ${media.above.md} {
        width: ${maxContentWidth + fixedPadding * 2}px;
        ${gridOrganizers(2, gridCols, gridRows, customChildGrids)}
    }
`;

const LayoutStyles = {
    "8": {
        style: gridStyles(
            [
                "minmax(0, 1fr)",
                "minmax(0, 1fr) minmax(0, 10fr) minmax(0, 1fr)",
                "minmax(0, 2fr) minmax(0, 8fr) minmax(0, 2fr)"
            ],
            [],
            [
                {},
                {
                    1: `grid-column-start: 2`
                },
                {
                    1: `grid-column-start: 2`
                }
            ],
            true
        ),
        count: 1
    },
    "10": {
        style: gridStyles(
            [
                "minmax(0, 1fr)",
                "minmax(0, 1fr) minmax(0, 10fr) minmax(0, 1fr)",
                "minmax(0, 1fr) minmax(0, 10fr) minmax(0, 1fr)"
            ],
            [],
            [
                {},
                {
                    1: `grid-column-start: 2`
                },
                {
                    1: `grid-column-start: 2`
                }
            ],
            true
        ),
        count: 1
    },
    "12": { style: gridStyles(Array(5).fill("minmax(0, 1fr)")), count: 1 },
    "6-6": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0, 1fr) minmax(0, 1fr)",
            "minmax(0, 1fr) minmax(0, 1fr)"
        ]),
        count: 2
    },
    "4-4-4": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "repeat(3, minmax(0, 1fr))",
            "repeat(3, minmax(0, 1fr))"
        ]),
        count: 3
    },
    "3-3-3-3": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0, 1fr) minmax(0, 1fr)",
            "repeat(4, minmax(0, 1fr))"
        ]),
        count: 4
    },
    "2-2-2-2-2-2": {
        style: gridStyles([
            "minmax(0, 1fr) minmax(0, 1fr)",
            "minmax(0, 1fr) minmax(0, 1fr)",
            "repeat(6, minmax(0, 1fr))"
        ]),
        count: 6
    },
    "4-8": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0px, 0.3333fr) minmax(0px, .6666fr)",
            "minmax(0px, 0.3333fr) minmax(0px, .6666fr)"
        ]),
        count: 2
    },
    "7-5": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0px, 1fr) minmax(0px, 1fr)",
            "minmax(0px, .5833fr) minmax(0px, 0.4166fr)"
        ]),
        count: 2
    },
    "3-9": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0, 1fr)",
            "minmax(0px, .25fr) minmax(0px, .75fr)"
        ]),
        count: 2
    },
    "8-4": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0px, .6666fr) minmax(0px, 0.3333fr)",
            "minmax(0px, .6666fr) minmax(0px, 0.3333fr)"
        ]),
        count: 2
    },
    "9-3": {
        style: gridStyles([
            "minmax(0, 1fr)",
            "minmax(0, 1fr)",
            "minmax(0px, 0.75fr) minmax(0px, .25fr)"
        ]),
        count: 2
    },
    "3-3-6": {
        style: gridStyles(
            [
                "minmax(0, 1fr) minmax(0, 1fr)",
                "minmax(0, 1fr) minmax(0, 1fr)",
                "minmax(0px, 1fr) minmax(0px, 1fr) minmax(0px, 2fr)"
            ],
            [
                "minmax(0, 1fr) minmax(0, auto)",
                "minmax(0, 1fr) minmax(0, auto)",
                "auto"
            ],
            [
                {
                    3: `grid-area: 2 / 1 / 3 / 3;`
                },
                {
                    3: `grid-area: 2 / 1 / 3 / 3;`
                },
                {
                    3: `grid-area: auto`
                }
            ]
        ),
        count: 3
    },
    "6-3-3": {
        style: gridStyles(
            [
                "minmax(0, 1fr) minmax(0, 1fr)",
                "minmax(0, 1fr) minmax(0, 1fr)",
                "minmax(0px, 2fr) minmax(0px, 1fr) minmax(0px, 1fr)"
            ],
            [
                "minmax(0, auto) minmax(0, 1fr)",
                "minmax(0, auto) minmax(0, 1fr)",
                "auto"
            ],
            [
                {
                    1: `grid-area: 1 / 1 / 1 / 3;`,
                    2: `grid-area: 2 / 1 / 3 / 2;`,
                    3: `grid-area: 2 / 2 / 3 / 3;`
                },
                {
                    1: `grid-area: 1 / 1 / 1 / 3;`,
                    2: `grid-area: 2 / 1 / 3 / 2;`,
                    3: `grid-area: 2 / 2 / 3 / 3;`
                },
                {
                    1: `grid-area: auto;`,
                    2: `grid-area: auto;`,
                    3: `grid-area: auto;`
                }
            ]
        ),
        count: 3
    }
};

type Entries<T> = {
    [K in keyof T]: [K, T[K]];
}[keyof T][];

const getEntries = <T extends object>(obj: T) =>
    Object.entries(obj) as Entries<T>;

const DefaultStyles = Object.fromEntries(
    getEntries(LayoutStyles)
        .slice(0, 5)
        .map(([key, val]) => [val["count"], key])
);

Object.freeze(LayoutStyles);
Object.freeze(DefaultStyles);

export type layoutRevised = keyof typeof LayoutStyles;

const verticalAlignment = {
    default: "stretch",
    top: "start",
    bottom: "end",
    center: "center"
};

type AlignTypes = "default" | "top" | "bottom" | "center";

export interface RowRevisedProps {
    layout?: layoutRevised;
    alignItems?: AlignTypes;
}

const getLayoutStyles = (childCount: number, layout?: layoutRevised) => {
    if (layout === undefined) {
        layout = DefaultStyles[childCount];
        if (layout === undefined) {
            throw new Error(`${childCount} is an invalid number of children`);
        }
    }
    if (childCount != LayoutStyles[layout]["count"]) {
        throw new Error(
            `Row with layout "${layout}" should have ${LayoutStyles[layout]["count"]} children`
        );
    }

    return LayoutStyles[layout]["style"];
};

export const RowRevised = (props: PropsWithChildren<RowRevisedProps>) => {
    const { layout } = props;
    const { alignItems } = props;
    const children = Children.toArray(props.children);
    const style = getLayoutStyles(children.length, layout);

    return (
        <div
            className="row-layout"
            css={css`
                ${style}
                align-items: ${alignItems
                    ? verticalAlignment[alignItems]
                    : verticalAlignment.default};
            `}
        >
            {children.map((child) => child)}
        </div>
    );
};
